import { isNil, pick } from "lodash-es";
import { booleanType, ensure, ensureArray, numberType, stringType, TType } from "types";

import {
  configDatabaseRecordType,
  configIdType,
  configPageableMetaType,
  IDatabaseRecord,
  idType,
  IId,
  IPageable,
  IPartialPageable,
} from "@/core/api";
import { dateType, TDate } from "@/core/dates";
import { attachmentType, IAttachment } from "@/data/attachments";
import { ICodelist } from "@/data/codelists";
import { companyType, ICompany, IRequestToJoinCompany, requestToJoinCompanyType } from "@/data/companies";
import { IInvitation, invitationType } from "@/data/invitations/types";
import { TJobApplicationStatuses } from "@/data/job-applications";
import { TEmailNotificationFrequency } from "@/data/job-requests";
import { ILanguage } from "@/data/languages";
import { ILocation, locationType } from "@/data/locations";
import {
  configPersonalInvitationContact,
  IPersonalInvitation,
  IPersonalInvitationContact,
  personalInvitationType,
} from "@/data/personal-invitations";
import { IPhone, phoneType } from "@/data/phones";
import { consultantProfileRoleType, IConsultantProfileRole } from "@/data/roles";
import { TUserRole } from "@/data/session";
import { ISkill, skillType } from "@/data/skills";

import { MAX_WORKSTYLES_POINTS } from "./constants";

export interface IConsultantProfileSkillDto {
  skill: ISkill;
}

export interface IConsultantProfileSkill extends IId {
  skill: ISkill;
}

const configConsultantProfileSkillType: TType<IConsultantProfileSkill> = {
  ...configIdType,
  skill: skillType,
};

export const consultantProfileSkillType = ensure(configConsultantProfileSkillType);

export interface IConsultantProfileLanguage extends IId {
  language: ILanguage;
  level: ICodelist["code"] | null;
}

const configConsultantProfileLanguageType: TType<IConsultantProfileLanguage> = {
  ...configIdType,
  language: stringType(null),
  level: stringType(null),
};

export const consultantProfileLanguageType = ensure(configConsultantProfileLanguageType);

export interface ISharedProfile extends IId {
  consultantProfile: IId;
  company: ICompany;
}

const configSharedProfileType: TType<ISharedProfile> = {
  ...configIdType,
  consultantProfile: idType,
  company: companyType,
};

export const sharedProfileType = ensure(configSharedProfileType);

export interface IContactPerson extends IId {
  email: string;
  firstName: string;
  lastName: string;
  phone: IPhone | null;
  location: ILocation | null;
  avatar: IAttachment | null;
  position: { name: string };
}

const configContactPerson: TType<IContactPerson> = {
  ...configIdType,
  email: stringType(""),
  firstName: stringType(""),
  lastName: stringType(""),
  position: ensure({ name: stringType("") }),
  phone: v => (isNil(v) ? null : phoneType(v)),
  location: v => (isNil(v) ? null : locationType(v)),
  avatar: v => (isNil(v) ? null : attachmentType(v)),
};

export const contactPersonType = ensure(configContactPerson);

export interface IConsultantProfileSettings {
  createFilterWidgetClosed: boolean;
  searchable: boolean;
  dailyJobsFrequency: TEmailNotificationFrequency;
}

export interface IConsultantProfileRolePayload {
  companyRole: TUserRole;
}

export interface IAboutMe {
  professionalSummary?: string;
  reasonOfBeingProfessionalist?: string;
  perfectCoworkersProfile?: string;
}

const configAboutMe: TType<IAboutMe> = {
  professionalSummary: v => (isNil(v) ? null : stringType("")(v)),
  reasonOfBeingProfessionalist: v => (isNil(v) ? null : stringType("")(v)),
  perfectCoworkersProfile: v => (isNil(v) ? null : stringType("")(v)),
};

export const aboutMeType = ensure(configAboutMe);

const configSettings: TType<IConsultantProfileSettings> = {
  createFilterWidgetClosed: booleanType(false),
  searchable: booleanType(false),
  dailyJobsFrequency: stringType("NEVER"),
};

export const settingsType = ensure(configSettings);

export type TConsultantProfileStatus = "PENDING" | "ACCEPTED" | "NOT_INVITED";

export type TWorkStyleType =
  | "PERFORMER"
  | "LEADER"
  | "TEAM_PLAYER"
  | "COOL_HEADED"
  | "HARD_WORKING"
  | "INDEPENDENT"
  | "PROBLEM_SOLVER";

export interface IWorkStyle {
  type: TWorkStyleType;
  score: number;
}

export const configConsultantProfileWorkStyleType: TType<IWorkStyle> = {
  type: stringType(""),
  score: numberType(0),
};

export const initialWorkstyles = [
  "PERFORMER",
  "LEADER",
  "TEAM_PLAYER",
  "COOL_HEADED",
  "HARD_WORKING",
  "INDEPENDENT",
  "PROBLEM_SOLVER",
].map(type => ({
  type,
  score: 0,
}));

export const consultantProfileWorkStyleType = ensure(configConsultantProfileWorkStyleType);

export type TCompletenessItemKey =
  | "ROLE"
  | "POSITION_AND_ROLE"
  | "POSITION"
  | "CV"
  | "SKILL"
  | "LANGUAGE"
  | "LOCATION"
  | "PHONE"
  | "ABOUT"
  | "AVATAR"
  | "WORKSTYLE";

export type TCompletenessLevel = "FRESH" | "GROWING" | "ESTABLISHED" | "PRIME";

export interface IProfileCompleteness {
  level: TCompletenessLevel;
  score: number;
  completed: TCompletenessItemKey[];
}

const configProfileCompletenessType: TType<IProfileCompleteness> = {
  level: v => stringType("FRESH")(v),
  score: v => numberType(0)(v),
  completed: ensureArray(stringType(null)),
};

const configProfileCompleteness = ensure(configProfileCompletenessType);

export interface IConsultantProfile extends IDatabaseRecord {
  aboutMe: IAboutMe | null;
  avatar: IAttachment | null;
  attachments: IAttachment[];
  availability: ICodelist["code"];
  availabilityDate: TDate | null;
  companyRole: TUserRole | null;
  completeness: IProfileCompleteness;
  contactPerson: IContactPerson;
  cvs: IAttachment[];
  email: string;
  pendingEmail?: string;
  firstName: string;
  companyInvitation: IInvitation | null;
  lastName: string;
  languages: IConsultantProfileLanguage[];
  location: ILocation | null;
  phone: IPhone | null;
  position: { name: string };
  roles: IConsultantProfileRole[];
  settings: IConsultantProfileSettings;
  skills: IConsultantProfileSkill[];
  sharedProfile: ISharedProfile | null;
  requestToJoin: IRequestToJoinCompany | null;
  status: TConsultantProfileStatus;
  workStyles: IWorkStyle[] | null;
  workPreferences: Array<ICodelist["code"]>;
  newSuggestedData: boolean;
  personalInvitationContactPerson: IPersonalInvitationContact | null;
  personalInvitation: IPersonalInvitation | null;
  notDisplayedNotificationCount: number | null;
  notDisplayedTaskCount: number | null;
  parent: IId | null;
  applicationCount: number;
  firstApplicationStatus: TJobApplicationStatuses | null;
  draftApplicationId: number | null;
  blocked: boolean;
  reasonOfBlock: string | null;
}

export interface IConsultantProfileDto extends Partial<IConsultantProfile> {}

export const configConsultantProfileType: TType<IConsultantProfile> = {
  ...configDatabaseRecordType,
  avatar: v => (isNil(v) ? null : attachmentType(v)),
  attachments: ensureArray(attachmentType),
  availability: stringType(null),
  availabilityDate: dateType(),
  companyRole: v => (isNil(v) ? null : stringType("")(v)),
  completeness: configProfileCompleteness,
  contactPerson: contactPersonType,
  cvs: ensureArray(attachmentType),
  email: stringType(""),
  pendingEmail: v => (isNil(v) ? null : stringType("")(v)),
  firstName: stringType(""),
  companyInvitation: v => (isNil(v) ? null : invitationType(v)),
  lastName: stringType(""),
  position: ensure({ name: stringType("") }),
  languages: ensureArray(consultantProfileLanguageType),
  location: v => (isNil(v) ? null : locationType(v)),
  phone: v => (isNil(v) ? null : phoneType(v)),
  aboutMe: aboutMeType,
  roles: ensureArray(consultantProfileRoleType),
  settings: settingsType,
  skills: ensureArray(consultantProfileSkillType),
  sharedProfile: v => (isNil(v) ? null : sharedProfileType(v)),
  status: stringType(""),
  requestToJoin: v => (isNil(v) ? null : requestToJoinCompanyType(v)),
  workPreferences: ensureArray(stringType(null)),
  workStyles: v =>
    isNil(v) ||
    v.length === 0 ||
    v.reduce((acc: number, curr: { score: number }) => acc + curr.score, 0) !== MAX_WORKSTYLES_POINTS
      ? []
      : ensureArray(consultantProfileWorkStyleType)(v),
  newSuggestedData: booleanType(false),
  personalInvitationContactPerson: v => (isNil(v) ? null : ensure(configPersonalInvitationContact)(v)),
  personalInvitation: v => (isNil(v) ? null : personalInvitationType(v)),
  notDisplayedNotificationCount: v => (isNil(v) ? null : numberType(0)(v)),
  notDisplayedTaskCount: v => (isNil(v) ? null : numberType(0)(v)),
  parent: v => (isNil(v) ? null : idType(v)),
  applicationCount: numberType(0),
  firstApplicationStatus: v => (isNil(v) ? null : stringType("")(v)),
  draftApplicationId: v => (isNil(v) ? null : numberType(0)(v)),
  blocked: booleanType(false),
  reasonOfBlock: stringType(null),
};

export const consultantProfileType = ensure(configConsultantProfileType);

export interface IPageableConsultantProfile extends IPageable<IConsultantProfile[]> {}

export const pageableConsultantProfileTypeConfig: TType<IPageableConsultantProfile> = {
  ...configPageableMetaType,
  content: ensureArray(consultantProfileType),
};

export const pageableConsultantProfileType = ensure(pageableConsultantProfileTypeConfig);

export interface IPartialPageableConsultantProfile extends IPartialPageable<IConsultantProfile[]> {}

export type IShortConsultantProfile = Pick<
  IConsultantProfile,
  | "id"
  | "avatar"
  | "companyRole"
  | "email"
  | "firstName"
  | "lastName"
  | "position"
  | "requestToJoin"
  | "roles"
  | "status"
  | "companyInvitation"
>;

export const configShortConsultantProfileType: TType<IShortConsultantProfile> = pick(configConsultantProfileType, [
  "id",
  "avatar",
  "companyRole",
  "email",
  "firstName",
  "position",
  "lastName",
  "requestToJoin",
  "roles",
  "status",
  "companyInvitation",
]);

export const shortConsultantProfileType = ensure(configShortConsultantProfileType);

export interface IPageableShortConsultantProfile extends IPageable<IShortConsultantProfile[]> {}

export const pageableShortConsultantProfileTypeConfig: TType<IPageableShortConsultantProfile> = {
  ...configPageableMetaType,
  content: ensureArray(shortConsultantProfileType),
};

export const pageableShortConsultantProfileType = ensure(pageableShortConsultantProfileTypeConfig);

export interface IPartialPageableShortConsultantProfile extends IPartialPageable<IShortConsultantProfile[]> {}

export function getProfileRole(role: TUserRole | null, requestToJoin?: IRequestToJoinCompany | null) {
  if (requestToJoin && requestToJoin.status === "PENDING") {
    return "MANAGER";
  }

  switch (role) {
    case "CONSULTANT":
      return "CONSULTANT";
    case "MANAGER":
      return "MANAGER";
    case "ADMIN":
      return "ADMIN";
    default:
      return "PRIVATE_PERSON";
  }
}
